'use es6';

import { PureComponent } from 'react';
import PropTypes from 'prop-types';
import emptyFunction from 'react-utils/emptyFunction';
import { connect } from 'react-redux';
import { debounce } from 'underscore';
import FormattedMessage from 'I18n/components/FormattedMessage';
import text from 'I18n/utils/unescapedText';
import UISelect from 'UIComponents/input/UISelect';
import UITag from 'UIComponents/tag/UITag';
import UITooltip from 'UIComponents/tooltip/UITooltip';
import * as PagesAction from 'simple-menus-core/actions/Pages';
import { getPage, getPagesOptions, getPagesFetchRequestStatus } from 'simple-menus-core/selectors/pages';
import { DEFAULT_DEBOUNCE_TIME, REQUEST_STATUS } from 'simple-menus-core/constants/constants';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const PAGE_SIZE = 15;
const i18nKey = key => `simpleMenusLib.components.pagesLinkSelect.draft.${key}`;
function mapStateToProps(state, props) {
  const {
    pageLinkId
  } = props;
  return {
    page: getPage(state, {
      pageLinkId
    }),
    pagesOptions: getPagesOptions(state),
    pagesFetchRequestStatus: getPagesFetchRequestStatus(state)
  };
}
const mapDispatchToProps = {
  fetchPages: PagesAction.fetchPages,
  fetchPagesById: PagesAction.fetchPagesById,
  saveCachedPage: PagesAction.saveCachedPage,
  clearCache: PagesAction.clearCache
};
class PagesLinkSelect extends PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      input: '',
      isOpen: false,
      offset: 0,
      pagesRequestFulfilled: !!props.page
    };
    this.loadOptions = debounce(this.loadOptions.bind(this), DEFAULT_DEBOUNCE_TIME);
    this.onOpenChange = this.onOpenChange.bind(this);
    this.openLinkSelect = this.openLinkSelect.bind(this);
    this.onChange = this.onChange.bind(this);
    this.pageRequestRetried = false;
  }
  componentDidUpdate() {
    const {
      fetchPagesById,
      isVisible,
      page,
      pageLinkId,
      pagesFetchRequestStatus
    } = this.props;
    if (!this.state.pagesRequestFulfilled && isVisible && (pagesFetchRequestStatus === REQUEST_STATUS.SUCCEEDED || pagesFetchRequestStatus === REQUEST_STATUS.FAILED)) {
      if (!(page || this.pageRequestRetried)) {
        fetchPagesById(pageLinkId);
        this.pageRequestRetried = true;
        this.setState({
          pagesRequestFulfilled: false
        });
      } else {
        this.setState({
          pagesRequestFulfilled: true
        });
      }
    }
  }
  onOpenChange({
    target: {
      value: isOpen
    }
  }) {
    /* Allow dropdown to trigger onChange before switching */
    setTimeout(() => {
      this.setState({
        isOpen,
        offset: 0
      });
      if (!isOpen) this.props.clearCache();
    }, 0);
  }
  onChange(evt) {
    if (this.props.onChange) {
      this.props.onChange(evt);
    }
    const id = evt.target.value;
    this.props.saveCachedPage(id);
  }
  openLinkSelect() {
    this.setState({
      isOpen: true
    });
  }
  loadOptions(input, callback) {
    let {
      offset
    } = this.state;
    const {
      isOpen
    } = this.state;
    if (!isOpen) return;
    if (input !== this.state.input) {
      offset = 0;
      this.setState({
        input
      });
      this.props.clearCache();
    }
    const query = {
      offset,
      limit: PAGE_SIZE,
      name: input
    };
    this.props.fetchPages(query).then(response => {
      const {
        hasMore,
        total
      } = response;
      const {
        pagesOptions: options
      } = this.props;
      offset = offset + PAGE_SIZE;
      this.setState({
        offset
      });
      callback(null, {
        options,
        pagination: {
          hasMore,
          length: total,
          offset
        }
      });
    }).catch(err => {
      setTimeout(() => {
        throw err;
      });
    });
  }
  get className() {
    return 'pages-select' + (!this.state.isOpen ? " preview-label" : "");
  }
  get error() {
    const {
      pagesRequestFulfilled
    } = this.state;
    const {
      page,
      pageLinkId
    } = this.props;
    return pagesRequestFulfilled && pageLinkId && !page;
  }
  renderDraftTag() {
    if (this.props.isPublished || !this.props.editorUrl) return null;
    return /*#__PURE__*/_jsx(UITooltip, {
      title: text(i18nKey('tooltip')),
      disabled: this.props.readOnly,
      children: /*#__PURE__*/_jsx(UITag, {
        use: "warning",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: i18nKey('label')
        })
      })
    });
  }
  renderPlaceholder() {
    return /*#__PURE__*/_jsxs("div", {
      className: "placeholder",
      children: [this.renderDraftTag(), this.props.placeholder]
    });
  }
  render() {
    const {
      readOnly
    } = this.props;
    const {
      isOpen
    } = this.state;
    const placeholder = this.renderPlaceholder();
    if (!isOpen || readOnly) {
      return /*#__PURE__*/_jsx(UISelect, {
        error: this.error,
        onOpenChange: this.openLinkSelect,
        className: this.className,
        disabled: readOnly,
        placeholder: placeholder,
        value: null
      });
    }
    return /*#__PURE__*/_jsx(UISelect, {
      error: this.error,
      onChange: this.onChange,
      placeholder: placeholder,
      loadOptions: this.loadOptions,
      onOpenChange: this.onOpenChange,
      defaultOpen: true,
      className: this.className,
      filterOptions: false,
      cache: null
    });
  }
}
PagesLinkSelect.propTypes = {
  clearCache: PropTypes.func.isRequired,
  editorUrl: PropTypes.string,
  fetchPages: PropTypes.func.isRequired,
  fetchPagesById: PropTypes.func.isRequired,
  isPublished: PropTypes.bool,
  isVisible: PropTypes.bool.isRequired,
  onChange: PropTypes.func.isRequired,
  page: PropTypes.object,
  pageLinkId: PropTypes.number,
  pagesOptions: PropTypes.arrayOf(PropTypes.object),
  pagesFetchRequestStatus: PropTypes.string.isRequired,
  placeholder: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  readOnly: PropTypes.bool,
  saveCachedPage: PropTypes.func.isRequired
};
PagesLinkSelect.defaultProps = {
  onChange: emptyFunction
};
export default connect(mapStateToProps, mapDispatchToProps)(PagesLinkSelect);